/**
 * 도구 사용 메시지 파싱 관련 유틸리티
 */

// 도구 사용 결과 인터페이스
export interface ToolUseResult {
    name: string;
    input: Record<string, unknown>;
    isCompleted: boolean;
    specialType?: string;
}

// 노트 검색 결과 관련 인터페이스
interface NoteItem {
    note_id?: string;
    id?: string;
    title?: string;
    register_date?: string;
    date?: string;
    author_name?: string;
}

interface NoteRow {
    rows?: NoteItem[];
}

interface NoteSearchResult {
    results?: NoteRow[];
    rows?: NoteItem[];
    success?: boolean;
    data?: unknown[];
    error?: unknown;
    rowcount?: number;
    status?: string;
}

// 할 일 관련 인터페이스
interface TodoRow {
    todo_id: string;
    todo_title?: string;
    planner_id?: string;
    content?: string;
    due_date?: string | null;
    due_date_kst?: string | null;
    complete_date?: string | null;
    complete_date_kst?: string | null;
    goal_id?: string;
    goal_title?: string;
    category_name?: string;
    planner_date_kst?: string;
    blocks?: number[];
    register_date?: string;
    update_date?: string;
    enable?: boolean;
    current_block_index?: number;
    formatted_time?: string;
    start_time?: string;
    end_time?: string;
    duration?: string;
}

interface ResultSet {
    rows?: TodoRow[];
}

// Record<string, unknown>을 확장하여 인덱스 시그니처 문제 해결
interface ParsedContent extends Record<string, unknown> {
    results?: ResultSet[];
    data?: TodoRow[];
    success?: boolean;
    error?: string | null;
    rowcount?: number;
    status?: string;
    report_format?: unknown;
    type?: string;
    todo_id?: string;
    planner_id?: string;
}

// 표준화된 노트 결과 인터페이스
interface FormattedNoteItem {
    id: string;
    title: string;
    date: string;
    url: string;
    author_name?: string;
}

/**
 * JSON 형식의 도구 사용 메시지를 파싱하는 유틸리티 함수
 */
export function parseToolUseMessage(
    message: string,
    isCellOutput = false,
    langId = 'n/a',
    isForCitations = false
): ToolUseResult | null {
    const isNotCompleted =
        message.includes('tool_call_id') && message.includes('streaming');

    if (
        message === '' ||
        message.trim() === '' ||
        (message.trim().startsWith('```') &&
            message.trim().endsWith('```') &&
            message.trim().length <= 6)
    ) {
        return null;
    }

    try {
        // 명백한 일반 텍스트인 경우 빠르게 처리를 중단
        // (짧은 텍스트, 한글/영문 텍스트, JSON 기호가 없는 경우)
        if (
            message.length < 50 &&
            !message.includes('{') &&
            !message.includes('}') &&
            !/^[[{"]/.test(message.trim())
        ) {
            // console.log('[ToolUseBox] 일반 텍스트로 판단하여 건너뜀:', message);
            return null;
        }

        // Try to parse the message as JSON directly
        try {
            const jsonData = JSON.parse(message) as Record<string, unknown>;
            console.log(
                '[ToolUseBox] 직접 JSON 파싱 성공:',
                Object.keys(jsonData)
            );

            // html_base64 또는 png_base64 키가 있는지 직접 확인 (최우선 확인)
            if (
                jsonData &&
                ('html_base64' in jsonData || 'png_base64' in jsonData)
            ) {
                console.log('[ToolUseBox] 시각화 결과 감지 - 최우선 처리:', {
                    has_html: !!jsonData.html_base64,
                    has_png: !!jsonData.png_base64,
                    filename: jsonData.filename || 'chart',
                    note_exists: !!jsonData.note,
                });

                // 결과를 그대로 전달하여 하나의 ToolUseBox에서 처리
                return {
                    name: 'data_for_visualization',
                    input: {
                        html_base64: jsonData.html_base64,
                        png_base64: jsonData.png_base64,
                        filename: jsonData.filename || 'chart',
                        note: jsonData.note,
                    },
                    isCompleted: true,
                    specialType: 'visualization_result',
                };
            }

            // 노트 검색 결과 확인 - results -> rows 구조
            const noteData = jsonData as NoteSearchResult;
            if (
                noteData.results &&
                Array.isArray(noteData.results) &&
                noteData.results[0]?.rows &&
                Array.isArray(noteData.results[0].rows) &&
                noteData.results[0].rows.length > 0
            ) {
                // note_id 필드가 있는지 확인
                const firstRow = noteData.results[0].rows[0];
                console.log('[ToolUseBox] 노트 검색 결과 감지:', firstRow);
                if (firstRow?.note_id) {
                    console.log(
                        '[ToolUseBox] 노트 검색 결과 감지 - results->rows 구조:',
                        {
                            results_count: noteData.results.length,
                            rows_count: noteData.results[0].rows.length,
                            sample_note_id: firstRow.note_id || firstRow.id,
                        }
                    );

                    // 결과를 rows 배열로 가공하여 반환
                    const formattedRows: FormattedNoteItem[] =
                        noteData.results[0].rows.map(row => ({
                            id: row.note_id || row.id || '',
                            title: row.title || '제목 없음',
                            date: row.register_date || row.date || '',
                            url: `/note/${row.note_id || row.id || ''}`,
                            author_name: row.author_name || '',
                        }));

                    return {
                        name: 'search_note',
                        input: { results: formattedRows },
                        isCompleted: true,
                        specialType: 'note_search_results',
                    };
                }
            }

            // get_todo_at_time 결과인지 확인
            // 1. 직접 todo_id 속성 체크
            if (
                jsonData.success === true &&
                jsonData.todo_id &&
                typeof jsonData.todo_id === 'string' &&
                jsonData.planner_id &&
                typeof jsonData.planner_id === 'string'
            ) {
                console.log(
                    '[ToolUseBox] 일정 조회 결과 감지 (직접 todo_id 확인):',
                    jsonData
                );
                    return {
                        name: 'get_todo_at_time',
                        input: jsonData,
                        isCompleted: true,
                    };
                }
                
            // 2. results 배열 내부에 todo_id가 있는지 체크
            if (
                jsonData.success === true &&
                Array.isArray(jsonData.results) &&
                jsonData.results.length > 0
            ) {
                // results 배열의 첫 번째 항목 확인
                // 타입 안전성을 위해 인터페이스 활용
                const resultSet = jsonData.results[0] as ResultSet;
                // 옵셔널 체이닝을 사용하여 객체 안전하게 접근
                if (
                    Array.isArray(resultSet?.rows) &&
                    resultSet.rows.length > 0
                ) {
                    // 타입 추론을 활용하여 불필요한 타입 단언 제거
                    const row = resultSet.rows[0];
                    // 옵셔널 체이닝으로 todo_id 접근
                    // todo_id가 있는지 확인
                    if (row.todo_id) {
                        // 추가 검증 로직 - todo_id와 planner_id 모두 확인
                        const hasTodoId = !!row.todo_id && typeof row.todo_id === 'string';
                        const hasPlannerId = !!row.planner_id;
                        
                        console.log(
                            '[ToolUseBox] 일정 조회 결과 감지 (results 배열에서 todo_id/planner_id 확인):',
                            {
                                hasTodoId,
                                hasPlannerId,
                                todoId: row.todo_id,
                                plannerId: row.planner_id
                            }
                        );

                        // todo_id와 planner_id 추출
                        const todoId = row.todo_id;
                        const plannerId = row.planner_id || null;
                        
                        // planner_id가 존재하는 경우에만 get_todo_at_time으로 처리
                        if (hasPlannerId) {
                            return {
                                name: 'get_todo_at_time',
                                input: {
                                    todo_id: todoId,
                                    planner_id: plannerId,
                                },
                                isCompleted: true,
                            };
                        }
                        
                        // planner_id가 없는 경우는 get_todo_at_time으로 처리하지 않음
                        console.log('[ToolUseBox] results 배열에서 planner_id가 없어 get_todo_at_time으로 처리하지 않음');
                    }
                }
            }
            // 할 일 생성 결과인지 확인 (success: true, todo_id 존재)
            if (
                jsonData.success === true &&
                jsonData.todo_id &&
                typeof jsonData.todo_id === 'string'
            ) {
                console.log(
                    '[ToolUseBox] 할 일 생성 결과 감지 (todo_id 확인):',
                    jsonData
                );
                return {
                    name: 'create_todo',
                    input: jsonData,
                    isCompleted: true,
                };
            }

            // data 배열 내부에 todo_id가 있는지 확인
            if (
                jsonData.success === true &&
                jsonData.data &&
                Array.isArray(jsonData.data) &&
                jsonData.data.length > 0
            ) {
                const dataItem = jsonData.data[0] as Record<string, unknown>;
                if (dataItem.todo_id && typeof dataItem.todo_id === 'string') {
                    console.log(
                        '[ToolUseBox] 할 일 생성 결과 감지 (data 배열에서 todo_id 확인):',
                        dataItem
                    );
                    // input에 직접 todo_id 추가
                    jsonData.todo_id = dataItem.todo_id;
                    return {
                        name: 'create_todo',
                        input: jsonData,
                        isCompleted: true,
                    };
                }
            }

            // 기타 JSON 데이터 처리
            return {
                name: 'unknown_json',
                input: jsonData,
                isCompleted: true,
            };
        } catch (e) {
            // JSON 파싱 실패시 계속 진행
            // 명백한 JSON이 아닌 경우 로그 출력 안함
            if (message.includes('{') && message.includes('}')) {
                console.log('[ToolUseBox] 직접 JSON 파싱 실패, 계속 진행');
            }
        }

        // 일반 텍스트가 너무 길면 시간 낭비 방지를 위해 빠르게 리턴
        if (
            message.length > 500 &&
            !/{"[^"]+"\s*:/.test(message) &&
            !message.includes('```json')
        ) {
            // console.log('[ToolUseBox] 긴 일반 텍스트로 판단하여 건너뜀:', message.substring(0, 50) + '...');
            return null;
        }

        // 코드 블록 내부에서 JSON 추출 시도
        let jsonStr = message;
        const codeBlockRegex = /```(?:json)?\s*([\s\S]*?)```/;
        const codeBlockMatches = codeBlockRegex.exec(message);

        if (codeBlockMatches?.[1]) {
            jsonStr = codeBlockMatches[1].trim();
            console.log(
                '[ToolUseBox] 코드 블록에서 JSON 추출:',
                jsonStr.substring(0, 50) + (jsonStr.length > 50 ? '...' : '')
            );
        }

        // JSON 객체 찾기 시도
        const jsonRegex = /(\{[\s\S]*\})/;
        const jsonMatches = jsonRegex.exec(jsonStr);

        if (jsonMatches?.[1]) {
            jsonStr = jsonMatches[1];
            console.log(
                '[ToolUseBox] JSON 형식 추출 성공:',
                jsonStr.substring(0, 50) + (jsonStr.length > 50 ? '...' : '')
            );
        }

        // 추출 결과가 명백한 JSON이 아니면 시도 안 함
        if (!/^\s*\{/.test(jsonStr)) {
            return null;
        }

        try {
            const data = JSON.parse(jsonStr) as Record<string, unknown>;
            console.log('[ToolUseBox] JSON 파싱 성공:', Object.keys(data));

            // 할 일 생성 결과인지 확인 (success: true, todo_id 존재)
            if (
                data.success === true &&
                data.todo_id &&
                typeof data.todo_id === 'string'
            ) {
                console.log(
                    '[ToolUseBox] 할 일 생성 결과 감지 (코드 블록, todo_id 확인):',
                    data
                );
                return {
                    name: 'create_todo',
                    input: data,
                    isCompleted: true,
                };
            }

            // data 배열 내부에 todo_id가 있는지 확인
            if (
                data.success === true &&
                data.data &&
                Array.isArray(data.data) &&
                data.data.length > 0
            ) {
                const dataItem = data.data[0] as Record<string, unknown>;
                if (dataItem.todo_id && typeof dataItem.todo_id === 'string') {
                    console.log(
                        '[ToolUseBox] 할 일 생성 결과 감지 (코드 블록, data 배열에서 todo_id 확인):',
                        dataItem
                    );
                    // input에 직접 todo_id 추가
                    data.todo_id = dataItem.todo_id;
                    return {
                        name: 'create_todo',
                        input: data,
                        isCompleted: true,
                    };
                }
            }

            // 시각화 결과 재확인
            if (data && ('html_base64' in data || 'png_base64' in data)) {
                console.log('[ToolUseBox] 시각화 결과 감지 (2차 확인):', {
                    has_html: !!data.html_base64,
                    has_png: !!data.png_base64,
                    filename: data.filename || 'chart',
                });

                return {
                    name: 'data_for_visualization',
                    input: {
                        html_base64: data.html_base64,
                        png_base64: data.png_base64,
                        filename: data.filename || 'chart',
                        note: data.note,
                    },
                    isCompleted: true,
                    specialType: 'visualization_result',
                };
            }

            // 기존 도구 호출 형식 처리
            let toolName = '';
            let toolInput: Record<string, unknown> = {};

            // 도구 이름 추출
            if (data && typeof data.name === 'string') toolName = data.name;
            else if (data && typeof data.tool === 'string')
                toolName = data.tool;
            else if (
                data &&
                typeof data.type === 'string' &&
                (data.type.includes('daily_report') ||
                    data.type.includes('weekly_report'))
            ) {
                // 보고서 형식 관련 특수 처리
                toolName = data.type.includes('daily')
                    ? 'daily_report_format'
                    : 'weekly_report_format';
            } else if (
                data &&
                'function_call' in data &&
                typeof data.function_call === 'object' &&
                data.function_call !== null &&
                'name' in data.function_call &&
                typeof data.function_call.name === 'string'
            ) {
                const functionCall = data.function_call as Record<
                    string,
                    unknown
                >;
                toolName = functionCall.name as string;
            }

            // 도구 입력 추출
            if (data && typeof data.input === 'object' && data.input !== null) {
                toolInput = data.input as Record<string, unknown>;
            } else if (
                data &&
                typeof data.arguments === 'object' &&
                data.arguments !== null
            ) {
                toolInput = data.arguments as Record<string, unknown>;
            } else if (
                data &&
                typeof data.parameters === 'object' &&
                data.parameters !== null
            ) {
                toolInput = data.parameters as Record<string, unknown>;
            } else if (
                data &&
                'function_call' in data &&
                typeof data.function_call === 'object' &&
                data.function_call !== null &&
                'arguments' in data.function_call
            ) {
                try {
                    const functionCall = data.function_call as Record<
                        string,
                        unknown
                    >;
                    if (typeof functionCall.arguments === 'string') {
                        // 불필요한 타입 어서션을 제거하고 타입 안전성 보장
                        const argsStr = functionCall.arguments;
                        toolInput = JSON.parse(argsStr) as Record<
                            string,
                            unknown
                        >;
                    } else if (
                        typeof functionCall.arguments === 'object' &&
                        functionCall.arguments !== null
                    ) {
                        toolInput = functionCall.arguments as Record<
                            string,
                            unknown
                        >;
                    }
                } catch (e) {
                    console.error('[ToolUseBox] 함수 호출 인자 파싱 오류:', e);
                    toolInput = { raw: data.function_call };
                }
            }

            if (toolName) {
                console.log('[ToolUseBox] 도구 사용 감지:', {
                    toolName,
                    inputKeys: Object.keys(toolInput || {}),
                });
                return {
                    name: toolName,
                    input: toolInput,
                    isCompleted: !isNotCompleted,
                };
            }

            return {
                name: 'unknown_tool',
                input: data,
                isCompleted: true,
            };
        } catch (e) {
            // 명백히 JSON인 경우에만 오류 출력
            if (
                jsonStr.trim().startsWith('{') &&
                jsonStr.trim().endsWith('}')
            ) {
                console.error('[ToolUseBox] JSON 파싱 실패 (2차):', e);
            }
        }

        return null;
    } catch (error) {
        // 명백히 JSON인 경우에만 오류 출력
        if (message.includes('{') && message.includes('}')) {
            console.error('[ToolUseBox] 도구 사용 메시지 파싱 오류:', error);
        }
        return null;
    }
}
